"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { keyMetrics } from "@/lib/data/homepage";
import { Counter } from "@/components/ui/counter";

/**
 * Key Metrics — 6 unique stat cards, each with a distinct visual
 * treatment (not a uniform grid). Mixed media: radials, bars,
 * gradients, sparklines.
 */
export function KeyMetrics() {
  return (
    <section id="key-metrics" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-gold)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-gold)]" />
            Key Metrics
          </span>
          <h2 className="mt-6">
            Performance That Speaks for Itself
          </h2>
        </Reveal>

        {/* Bento grid: featured + 5 supporting */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* FEATURED — big stat with massive typography */}
          <Reveal className="lg:col-span-1 lg:row-span-2">
            <div
              className="rounded-3xl glass p-8 h-full flex flex-col justify-between relative overflow-hidden card-glow"
              style={{ ["--glow" as string]: keyMetrics[0].accent }}
            >
              <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full blur-3xl opacity-30" style={{ background: keyMetrics[0].accent }} />
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">
                    Headline Result
                  </span>
                  <span
                    className="radial-progress"
                    style={
                      {
                        "--value": keyMetrics[0].progress,
                        "--size": "3.2rem",
                        "--thickness": "5px",
                        color: keyMetrics[0].accent,
                      } as React.CSSProperties
                    }
                    role="progressbar"
                  />
                </div>
                <div className="display-stat-lg" style={{ color: keyMetrics[0].accent }}>
                  <Counter to={500} suffix="%" />
                </div>
                <div className="mt-3 text-xl font-bold">{keyMetrics[0].label}</div>
                <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                  {keyMetrics[0].sub}
                </p>
              </div>
              <div className="relative mt-6 h-1.5 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                <div
                  className="h-full rounded-full animate-[shimmer_2s_ease-in-out_infinite]"
                  style={{ width: `${keyMetrics[0].progress}%`, background: keyMetrics[0].accent }}
                />
              </div>
            </div>
          </Reveal>

          {/* 5 supporting cards with varied treatments */}
          {keyMetrics.slice(1).map((m, i) => {
            const treatments = [
              { layout: "stat", accent: m.accent },
              { layout: "ring", accent: m.accent },
              { layout: "bar", accent: m.accent },
              { layout: "stat", accent: m.accent },
              { layout: "ring", accent: m.accent },
            ];
            const t = treatments[i % treatments.length];
            return (
              <Reveal key={m.label} delay={(i % 2) * 90}>
                <div
                  className="rounded-2xl glass p-6 h-full flex items-center gap-5 relative overflow-hidden card-glow"
                  style={{ ["--glow" as string]: m.accent }}
                >
                  {t.layout === "ring" && (
                    <div
                      className="radial-progress ml-auto shrink-0"
                      style={
                        {
                          "--value": m.progress,
                          "--size": "3.4rem",
                          "--thickness": "6px",
                          color: m.accent,
                        } as React.CSSProperties
                      }
                      role="progressbar"
                    />
                  )}
                  <div className="flex flex-col flex-1">
                    <span className="text-[1.7rem] font-bold font-mono-num leading-none tracking-tight" style={{ color: m.accent }}>
                      {m.value}
                    </span>
                    <span className="font-semibold mt-1.5 text-sm">{m.label}</span>
                    <span className="text-[0.7rem] text-[rgb(var(--fg-subtle))] mt-0.5 leading-snug">
                      {m.sub}
                    </span>
                  </div>
                  {t.layout === "bar" && (
                    <div className="ml-auto shrink-0 w-16">
                      <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold mb-1 text-right">Score</div>
                      <div className="h-1.5 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${m.progress}%`, background: m.accent }}
                        />
                      </div>
                      <div className="text-[0.65rem] font-mono-num mt-1 text-right" style={{ color: m.accent }}>{m.progress}/100</div>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
